import type {CommentDto, CommentsCategory} from '@domain/comments'
import type {AssetConfigurationDto} from '@hconnect/kilnfeature/src/types'
import {stringify} from 'query-string'

import {Api} from './api'

import type {UpmEntityType} from '@refra/modules/common/enums'
import type {
  AttachedDocument,
  AttachedDocumentFile,
  DistributedCacheResponse,
  PlantNode,
  Shutdown,
  UpmHierarchy
} from '@refra/modules/common/types'
import {
  Refractory,
  RefractoryClassificationDto,
  RefractoryManufacturer,
  RefractoryPaginated
} from '@refra/modules/refra-catalog/types'
import {
  AllowedAssetConfigDataNames,
  AllowedUpmLevels,
  AssetConfigurationDataDto,
  UpmFilter
} from '@refra/modules/refra-flexible-report/types'
import {
  KilnReplacement,
  DrillingSettingsDTO,
  MaterialInstallationDetails,
  KilnMaterialInstallation,
  BrickShapes,
  BrickCalculationResponse,
  KilnShutdownReport
} from '@refra/modules/refra-planning/types'
import {
  StaticPartComponentTypeEnum,
  StaticPartTypeEnum
} from '@refra/modules/refra-static-parts/enums'
import {
  StaticPartDTO,
  StaticReplacementDTO,
  StaticPartComponentDto
} from '@refra/modules/refra-static-parts/types'

export type Queries = typeof queries

export const queries = {
  upmPlantStructure: async ({
    plantUpmId,
    IncludeEquipment
  }: {
    plantUpmId: string
    IncludeEquipment?: boolean
  }) => {
    const path = `/upm/plants/${plantUpmId}/structure`
    const response = await Api.axiosInstance.get<PlantNode>(path, {params: {IncludeEquipment}})
    return response.data
  },
  upmGlobalStructure: async ({filters}: {filters?: UpmFilter[]}) => {
    const path = '/upm/global-hierarchy'
    const response = await Api.axiosInstance.post<UpmHierarchy>(path, {levels: filters})
    return response.data
  },
  assetConfigData: async ({
    upmType,
    productionDataNames,
    upmIds
  }: {
    upmType: AllowedUpmLevels
    productionDataNames: AllowedAssetConfigDataNames[]
    upmIds: string[]
  }) => {
    const path = `/asset-config/janus-production-data/upm-type/${upmType}/filtered`
    const response = await Api.axiosInstance.post<AssetConfigurationDataDto[]>(path, {
      productionDataNames,
      typeUpmIds: upmIds
    })
    return response.data
  },
  classifications: async (): Promise<RefractoryClassificationDto[]> => {
    const path = '/plants/refractory-product-data-catalog/classifications'
    const response = await Api.axiosInstance.get<RefractoryClassificationDto[]>(path)
    return response.data
  },
  refractories: async ({
    page,
    limit,
    ids,
    name,
    manufacturerIds,
    activeAfter
  }: {
    page: number
    limit: number
    ids?: number[]
    name?: string
    manufacturerIds?: number[]
    activeAfter?: string
  }): Promise<RefractoryPaginated> => {
    const path = '/plants/refractory-product-data-catalog/refractories'
    const response = await Api.axiosInstance.get<RefractoryPaginated>(path, {
      params: {page, limit, ids, name, ManufacturerIds: manufacturerIds, activeAfter},
      paramsSerializer: (params: string[]) => stringify(params, {arrayFormat: 'none'})
    })
    return response.data
  },
  refractory: async ({refractoryId}: {refractoryId: number}): Promise<Refractory> => {
    const path = `/plants/refractory-product-data-catalog/refractories/${refractoryId}`
    const response = await Api.axiosInstance.get<Refractory>(path, {
      paramsSerializer: (params: string[]) => stringify(params, {arrayFormat: 'none'})
    })
    return response.data
  },
  refractoriesWithCustomization: async ({
    plantUpmId,
    page,
    limit,
    ids,
    name,
    manufacturerIds,
    activeAfter
  }: {
    plantUpmId: string
    page: number
    limit: number
    ids?: number[]
    name?: string
    manufacturerIds?: number[]
    activeAfter?: string
  }): Promise<RefractoryPaginated> => {
    const path = `/plants/${plantUpmId}/refractory-product-data-catalog/refractories`
    const response = await Api.axiosInstance.get<RefractoryPaginated>(path, {
      params: {page, limit, ids, name, manufacturerIds, activeAfter},
      paramsSerializer: (params: string[]) => stringify(params, {arrayFormat: 'none'})
    })
    return response.data
  },
  manufacturers: async (): Promise<RefractoryManufacturer[]> => {
    const path = '/plants/refractory-product-data-catalog/manufacturers'
    const response = await Api.axiosInstance.get<RefractoryManufacturer[]>(path)
    return response.data
  },
  attachedDocumentFile: async ({
    document,
    shouldUsePreview
  }: {
    document: AttachedDocument
    shouldUsePreview: boolean
  }) => {
    const {id, fileName, url, mediaType, previewUrl} = document
    const fileUrl = shouldUsePreview && previewUrl ? previewUrl : url
    const path = `/documents/${fileUrl}`
    const response = await Api.axiosInstance.get<ArrayBuffer>(path, {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': mediaType,
        Accept: mediaType
      }
    })
    const blobUrl = URL.createObjectURL(new Blob([response.data]))
    return {
      id,
      name: fileName,
      contentType: mediaType,
      preview: blobUrl,
      isImage: mediaType.includes('image')
    }
  },
  attachedDocumentsFiles: async ({
    documents,
    shouldUsePreview
  }: {
    documents: AttachedDocument[]
    shouldUsePreview: boolean
  }) => {
    const attachedDocumentsQueries = documents.map(async (document) =>
      queries.attachedDocumentFile({document, shouldUsePreview})
    )
    const data: AttachedDocumentFile[] | undefined = await Promise.all(attachedDocumentsQueries)
    return data ?? []
  },
  shutdowns: async ({plantUpmId, kilnUpmId}: {plantUpmId: string; kilnUpmId: string}) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns`
    const response = await Api.axiosInstance.get<Shutdown[]>(path)
    return response.data
  },
  kilnReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement`
    const response = await Api.axiosInstance.get<KilnReplacement>(path)
    return response.data
  },
  shutdownReport: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/report`
    const response = await Api.axiosInstance.get<KilnShutdownReport>(path)
    return response.data
  },
  materialInstallationDetails: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    installationId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    installationId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}`
    const response = await Api.axiosInstance.get<MaterialInstallationDetails>(path)
    return response.data
  },
  assetConfig: async ({
    plantUpmId,
    assetUpmType,
    assetUpmId
  }: {
    plantUpmId: string
    assetUpmType: UpmEntityType
    assetUpmId: string
  }) => {
    const path = `/asset-config/kms-data/plant/${plantUpmId}/upm-type/${assetUpmType}`

    const response = await Api.axiosInstance.get<AssetConfigurationDto>(path, {
      params: {
        typeUpmId: assetUpmId
      }
    })
    return response.data
  },
  drillingSettings: async ({plantUpmId, kilnUpmId}: {plantUpmId: string; kilnUpmId: string}) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/settings`
    const response = await Api.axiosInstance.get<DrillingSettingsDTO>(path)
    return response.data
  },
  brickShapes: async () => {
    const path = '/plants/refractory-product-data-catalog/brick-shapes'
    const response = await Api.axiosInstance.get<BrickShapes[]>(path)
    return response.data
  },
  staticPartsList: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId?: string
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/static-parts`
    const response = await Api.axiosInstance.get<StaticPartDTO[]>(path, {
      params: {
        shutdownId
      }
    })
    return response.data
  },
  staticPartDetail: async ({
    plantUpmId,
    kilnUpmId,
    staticPartId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    staticPartId: string
    shutdownId?: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/static-parts/${staticPartId}`
    const response = await Api.axiosInstance.get<StaticPartDTO>(path, {
      params: {
        shutdownId
      }
    })
    return response.data
  },
  staticPartDocuments: async ({
    plantUpmId,
    kilnUpmId,
    staticPartId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    staticPartId: number
    shutdownId?: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-parts/${staticPartId}/documents`
    const response = await Api.axiosInstance.get<AttachedDocument[]>(path)
    return response.data
  },
  staticPartComponentDetail: async ({
    plantUpmId,
    kilnUpmId,
    staticPartId,
    componentId,
    shutdownId
  }: {
    plantUpmId: string
    kilnUpmId: string
    staticPartId: string
    componentId: number
    shutdownId?: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/static-parts/${staticPartId}/components/${componentId}`
    const response = await Api.axiosInstance.get<StaticPartComponentDto>(path, {
      params: {
        shutdownId
      }
    })
    return response.data
  },
  staticPartReplacement: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    componentId
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    componentId: number
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/static-replacements`
    const response = await Api.axiosInstance.get<StaticReplacementDTO[]>(path, {
      params: {
        componentId
      }
    })
    return response.data
  },
  staticPartsTypes: async () => {
    const path = '/plants/refractory-planner/kilns/static-parts/types'
    const response =
      await Api.axiosInstance.get<Record<StaticPartTypeEnum, StaticPartComponentTypeEnum[]>>(path)
    return response.data
  },
  comments: async ({
    plantCode,
    commentsCategory,
    commentCategoryInstanceIds
  }: {
    plantCode: string
    commentsCategory: CommentsCategory
    commentCategoryInstanceIds: (string | number)[]
  }) => {
    const path = `/comments/${plantCode}/${commentsCategory}`
    const response = await Api.axiosInstance.get<CommentDto[]>(path, {
      params: {commentCategoryInstanceIds: commentCategoryInstanceIds.join(',')},
      paramsSerializer: (params: string[]) => stringify(params, {arrayFormat: 'none'})
    })
    return response.data
  },
  kilnMaterialInstallations: async ({
    plantUpmId,
    kilnUpmId,
    date
  }: {
    plantUpmId: string
    kilnUpmId: string
    date?: string
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/material-installations`
    const response = await Api.axiosInstance.get<KilnMaterialInstallation[]>(path, {params: {date}})
    return response.data
  },
  getUserStorageValue: async ({keyIdentifier}: {keyIdentifier: string}) => {
    const path = '/distributeduserstorage'
    const response = await Api.axiosInstance.get<DistributedCacheResponse>(path, {
      params: {keyIdentifier}
    })
    return response.data
  },
  brickCalculation: async ({
    plantUpmId,
    kilnUpmId,
    shutdownId,
    installationId,
    brickShape1,
    brickShape2,
    keyBricksCount,
    useRefratechnikKeyBricks
  }: {
    plantUpmId: string
    kilnUpmId: string
    shutdownId: number
    installationId: number
    brickShape1: string
    brickShape2: string
    keyBricksCount: number
    useRefratechnikKeyBricks: boolean
  }) => {
    const path = `/plants/${plantUpmId}/refractory-planner/kilns/${kilnUpmId}/shutdowns/${shutdownId}/kiln-replacement/material-installations/${installationId}/brick-shapes`
    const response = await Api.axiosInstance.get<BrickCalculationResponse>(path, {
      params: {
        BrickShape1: brickShape1,
        BrickShape2: brickShape2,
        KeyBricksCount: keyBricksCount,
        UseRefratechnikKeyBricks: useRefratechnikKeyBricks
      }
    })
    return response.data
  }
}
